import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { 
  Plus, GripVertical, Edit2, Trash2, X, Save, Loader2, 
  Upload, ExternalLink, Star, MapPin, Clock, Globe,
  Coffee, Flower, Waves, Umbrella, Building, Heart
} from 'lucide-react';
import axios from '../../lib/axios';
import { API } from '../../App';
import { toast } from 'sonner';

// Icon mapping for categories
const ICON_OPTIONS = [
  { value: 'coffee', label: 'Coffee', icon: Coffee },
  { value: 'flower', label: 'Flower', icon: Flower },
  { value: 'waves', label: 'Waves', icon: Waves },
  { value: 'umbrella', label: 'Beach', icon: Umbrella },
  { value: 'building', label: 'Building', icon: Building },
  { value: 'heart', label: 'Heart', icon: Heart },
  { value: 'star', label: 'Star', icon: Star },
];

// Color gradient options
const GRADIENT_OPTIONS = [
  { value: 'from-amber-500 to-orange-500', label: 'Orange', preview: 'bg-gradient-to-r from-amber-500 to-orange-500' },
  { value: 'from-emerald-500 to-teal-500', label: 'Teal', preview: 'bg-gradient-to-r from-emerald-500 to-teal-500' },
  { value: 'from-cyan-500 to-blue-500', label: 'Blue', preview: 'bg-gradient-to-r from-cyan-500 to-blue-500' },
  { value: 'from-purple-500 to-pink-500', label: 'Purple', preview: 'bg-gradient-to-r from-purple-500 to-pink-500' },
  { value: 'from-rose-500 to-red-500', label: 'Red', preview: 'bg-gradient-to-r from-rose-500 to-red-500' },
  { value: 'from-lime-500 to-green-500', label: 'Green', preview: 'bg-gradient-to-r from-lime-500 to-green-500' },
];

const AdminHotspots = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPartner, setEditingPartner] = useState(null);
  const [showForm, setShowForm] = useState(false);

  // Fetch partners
  const fetchPartners = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/hotspots?active_only=false`);
      const sorted = (response.data.hotspots || []).sort((a, b) => a.sort_order - b.sort_order);
      setPartners(sorted);
    } catch (error) {
      console.error('Error fetching partners:', error);
      toast.error('Kon partners niet laden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPartners();
  }, []);

  // Handle drag end
  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const items = Array.from(partners);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update sort_order for all items
    const updatedItems = items.map((item, index) => ({
      ...item,
      sort_order: index + 1
    }));

    setPartners(updatedItems);

    // Save new order to backend
    try {
      await Promise.all(
        updatedItems.map(item =>
          axios.put(`${API}/hotspots/${item.id}`, { sort_order: item.sort_order })
        )
      );
      toast.success('Volgorde opgeslagen');
    } catch (error) {
      console.error('Error saving order:', error);
      toast.error('Volgorde opslaan mislukt');
      fetchPartners(); // Revert on error
    }
  };

  // Delete partner
  const handleDelete = async (id) => {
    if (!window.confirm('Weet je zeker dat je deze partner wilt verwijderen?')) return;

    try {
      await axios.delete(`${API}/hotspots/${id}`);
      setPartners(partners.filter(p => p.id !== id));
      toast.success('Partner verwijderd');
    } catch (error) {
      console.error('Error deleting partner:', error);
      toast.error('Verwijderen mislukt');
    }
  };

  // Toggle active status
  const toggleActive = async (partner) => {
    try {
      await axios.put(`${API}/hotspots/${partner.id}`, { active: !partner.active });
      setPartners(partners.map(p => 
        p.id === partner.id ? { ...p, active: !p.active } : p
      ));
      toast.success(partner.active ? 'Partner gedeactiveerd' : 'Partner geactiveerd');
    } catch (error) {
      console.error('Error toggling status:', error);
      toast.error('Status wijzigen mislukt');
    }
  };

  // Open edit form
  const openEdit = (partner) => {
    setEditingPartner(partner);
    setShowForm(true);
  };

  // Open new partner form
  const openNew = () => {
    setEditingPartner(null);
    setShowForm(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-hotspots">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Featured Partners</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Beheer de featured partners op de Hotspots pagina
          </p>
        </div>
        <button
          onClick={openNew}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-opacity"
          data-testid="add-partner-btn"
        >
          <Plus className="w-4 h-4" />
          Nieuwe Partner
        </button>
      </div>

      {/* Partners List */}
      {partners.length === 0 ? (
        <div className="text-center py-12 bg-secondary/30 rounded-xl border border-border">
          <Building className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">Geen partners</h3>
          <p className="text-muted-foreground mb-4">Voeg je eerste featured partner toe</p>
          <button
            onClick={openNew}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg"
          >
            Partner Toevoegen
          </button>
        </div>
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="partners">
            {(provided) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                className="space-y-3"
              >
                {partners.map((partner, index) => (
                  <Draggable key={partner.id} draggableId={partner.id} index={index}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        className={`bg-secondary/50 rounded-xl border border-border p-4 transition-all ${
                          snapshot.isDragging ? 'shadow-lg ring-2 ring-primary' : ''
                        } ${!partner.active ? 'opacity-60' : ''}`}
                      >
                        <div className="flex items-center gap-4">
                          {/* Drag Handle */}
                          <div
                            {...provided.dragHandleProps}
                            className="cursor-grab active:cursor-grabbing p-1 hover:bg-secondary rounded"
                          >
                            <GripVertical className="w-5 h-5 text-muted-foreground" />
                          </div>

                          {/* Image */}
                          <div className="w-20 h-14 rounded-lg overflow-hidden bg-secondary flex-shrink-0">
                            {partner.image_url ? (
                              <img
                                src={partner.image_url}
                                alt={partner.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Building className="w-6 h-6 text-muted-foreground" />
                              </div>
                            )}
                          </div>

                          {/* Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold text-foreground truncate">
                                {partner.name}
                              </h3>
                              <span className={`px-2 py-0.5 text-xs rounded-full bg-gradient-to-r ${partner.color_gradient} text-white`}>
                                {partner.category}
                              </span>
                              {!partner.active && (
                                <span className="px-2 py-0.5 text-xs rounded-full bg-red-500/20 text-red-400">
                                  Inactief
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground truncate mt-0.5">
                              {partner.location}
                            </p>
                            <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
                                {partner.google_rating} ({partner.review_count})
                              </span>
                              {partner.website && (
                                <a
                                  href={partner.website}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-1 hover:text-primary"
                                >
                                  <Globe className="w-3 h-3" />
                                  Website
                                </a>
                              )}
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => toggleActive(partner)}
                              className={`p-2 rounded-lg transition-colors ${
                                partner.active
                                  ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                                  : 'bg-secondary text-muted-foreground hover:bg-secondary/80'
                              }`}
                              title={partner.active ? 'Deactiveren' : 'Activeren'}
                            >
                              <div className={`w-4 h-4 rounded-full border-2 ${
                                partner.active ? 'bg-green-400 border-green-400' : 'border-muted-foreground'
                              }`} />
                            </button>
                            <button
                              onClick={() => openEdit(partner)}
                              className="p-2 bg-secondary hover:bg-secondary/80 rounded-lg transition-colors"
                              title="Bewerken"
                            >
                              <Edit2 className="w-4 h-4 text-muted-foreground" />
                            </button>
                            <button
                              onClick={() => handleDelete(partner.id)}
                              className="p-2 bg-red-500/20 hover:bg-red-500/30 rounded-lg transition-colors"
                              title="Verwijderen"
                            >
                              <Trash2 className="w-4 h-4 text-red-400" />
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}

      {/* Edit/Create Modal */}
      {showForm && (
        <PartnerForm
          partner={editingPartner}
          onClose={() => setShowForm(false)}
          onSaved={() => {
            setShowForm(false);
            fetchPartners();
          }}
        />
      )}
    </div>
  );
};

/**
 * Partner Form Modal
 */
const PartnerForm = ({ partner, onClose, onSaved }) => {
  const [form, setForm] = useState({
    name: partner?.name || '',
    description: partner?.description || '',
    website: partner?.website || '',
    google_rating: partner?.google_rating || 5.0,
    review_count: partner?.review_count || 0,
    location: partner?.location || '',
    hours: partner?.hours || '',
    category: partner?.category || '',
    icon: partner?.icon || 'star',
    image_url: partner?.image_url || '',
    tags: partner?.tags?.join(', ') || '',
    color_gradient: partner?.color_gradient || 'from-amber-500 to-orange-500',
    active: partner?.active ?? true,
    start_date: partner?.start_date || '',
    end_date: partner?.end_date || '',
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(partner?.image_url || null);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.name.trim() || !form.category.trim()) {
      toast.error('Naam en categorie zijn verplicht');
      return;
    }

    setSaving(true);

    try {
      // Prepare data
      const submitData = {
        ...form,
        tags: form.tags.split(',').map(t => t.trim()).filter(Boolean),
        google_rating: parseFloat(form.google_rating) || 0,
        review_count: parseInt(form.review_count) || 0,
      };

      let savedPartner;

      if (partner?.id) {
        // Update existing
        const response = await axios.put(`${API}/hotspots/${partner.id}`, submitData);
        savedPartner = response.data;
      } else {
        // Create new
        const response = await axios.post(`${API}/hotspots`, submitData);
        savedPartner = response.data;
      }

      // Upload image if selected
      if (imageFile && savedPartner.id) {
        setUploadingImage(true);
        const formData = new FormData();
        formData.append('file', imageFile);
        
        try {
          const uploadResponse = await axios.post(`${API}/upload?folder_type=hotspots`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          
          console.log('Upload response:', uploadResponse.data);
          
          // Update partner with new image URL
          if (uploadResponse.data.url) {
            await axios.put(`${API}/hotspots/${savedPartner.id}`, {
              image_url: uploadResponse.data.url
            });
            toast.success('Afbeelding geüpload!');
            console.log('Updated partner with image:', uploadResponse.data.url);
          }
        } catch (uploadError) {
          console.error('Image upload failed:', uploadError);
          toast.error('Afbeelding upload mislukt, maar partner is opgeslagen');
        }
      }

      toast.success(partner ? 'Partner bijgewerkt' : 'Partner aangemaakt');
      onSaved();
    } catch (error) {
      console.error('Error saving partner:', error);
      toast.error('Opslaan mislukt');
    } finally {
      setSaving(false);
      setUploadingImage(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-background border border-border rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h3 className="text-lg font-semibold">
            {partner ? 'Partner Bewerken' : 'Nieuwe Partner'}
          </h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-secondary rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4 overflow-y-auto max-h-[calc(90vh-130px)]">
          {/* Image Upload */}
          <div>
            <label className="block text-sm font-medium mb-2">Afbeelding</label>
            <div className="flex items-start gap-4">
              <div className="w-32 h-20 rounded-lg bg-secondary overflow-hidden border-2 border-dashed border-border">
                {imagePreview ? (
                  <img src={imagePreview} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Building className="w-8 h-8 text-muted-foreground" />
                  </div>
                )}
              </div>
              <div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="hidden"
                  id="partner-image"
                />
                <label
                  htmlFor="partner-image"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground text-sm rounded-lg cursor-pointer hover:opacity-90"
                >
                  <Upload className="w-4 h-4" />
                  Upload Afbeelding
                </label>
                <p className="text-xs text-muted-foreground mt-2">
                  Of plak een URL hieronder
                </p>
                <input
                  type="url"
                  value={form.image_url}
                  onChange={(e) => {
                    setForm(prev => ({ ...prev, image_url: e.target.value }));
                    setImagePreview(e.target.value);
                    setImageFile(null);
                  }}
                  placeholder="https://..."
                  className="mt-1 w-full px-3 py-1.5 bg-secondary border border-border rounded-lg text-sm"
                />
              </div>
            </div>
          </div>

          {/* Name & Category Row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Naam *</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="House of Stroop"
                required
                className="w-full px-3 py-2 bg-secondary border border-border rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Categorie *</label>
              <input
                type="text"
                value={form.category}
                onChange={(e) => setForm(prev => ({ ...prev, category: e.target.value }))}
                placeholder="Coffee & Treats"
                required
                className="w-full px-3 py-2 bg-secondary border border-border rounded-lg"
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium mb-2">Beschrijving</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Een korte beschrijving van de partner..."
              rows={3}
              className="w-full px-3 py-2 bg-secondary border border-border rounded-lg resize-none"
            />
          </div>

          {/* Location & Hours */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Locatie</label>
              <input
                type="text"
                value={form.location}
                onChange={(e) => setForm(prev => ({ ...prev, location: e.target.value }))}
                placeholder="Kura Hulanda Village, Willemstad"
                className="w-full px-3 py-2 bg-secondary border border-border rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Openingstijden</label>
              <input
                type="text"
                value={form.hours}
                onChange={(e) => setForm(prev => ({ ...prev, hours: e.target.value }))}
                placeholder="Mon-Sat: 9AM - 5PM"
                className="w-full px-3 py-2 bg-secondary border border-border rounded-lg"
              />
            </div>
          </div>

          {/* Website */}
          <div>
            <label className="block text-sm font-medium mb-2">Website</label>
            <input
              type="url"
              value={form.website}
              onChange={(e) => setForm(prev => ({ ...prev, website: e.target.value }))}
              placeholder="https://www.example.com"
              className="w-full px-3 py-2 bg-secondary border border-border rounded-lg"
            />
          </div>

          {/* Rating & Reviews */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Google Rating</label>
              <input
                type="number"
                value={form.google_rating}
                onChange={(e) => setForm(prev => ({ ...prev, google_rating: e.target.value }))}
                min="0"
                max="5"
                step="0.1"
                className="w-full px-3 py-2 bg-secondary border border-border rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Aantal Reviews</label>
              <input
                type="number"
                value={form.review_count}
                onChange={(e) => setForm(prev => ({ ...prev, review_count: e.target.value }))}
                min="0"
                className="w-full px-3 py-2 bg-secondary border border-border rounded-lg"
              />
            </div>
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium mb-2">Tags (komma gescheiden)</label>
            <input
              type="text"
              value={form.tags}
              onChange={(e) => setForm(prev => ({ ...prev, tags: e.target.value }))}
              placeholder="Coffee, Dutch Treats, Stroopwafels"
              className="w-full px-3 py-2 bg-secondary border border-border rounded-lg"
            />
          </div>

          {/* Icon & Color */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Icoon</label>
              <div className="flex flex-wrap gap-2">
                {ICON_OPTIONS.map(({ value, label, icon: Icon }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setForm(prev => ({ ...prev, icon: value }))}
                    className={`p-2 rounded-lg border transition-colors ${
                      form.icon === value
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-secondary border-border hover:border-primary/50'
                    }`}
                    title={label}
                  >
                    <Icon className="w-4 h-4" />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Kleur</label>
              <div className="flex flex-wrap gap-2">
                {GRADIENT_OPTIONS.map(({ value, label, preview }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setForm(prev => ({ ...prev, color_gradient: value }))}
                    className={`w-8 h-8 rounded-lg ${preview} transition-transform ${
                      form.color_gradient === value
                        ? 'ring-2 ring-white ring-offset-2 ring-offset-background scale-110'
                        : 'hover:scale-105'
                    }`}
                    title={label}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Schedule */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Start Datum (optioneel)</label>
              <input
                type="date"
                value={form.start_date}
                onChange={(e) => setForm(prev => ({ ...prev, start_date: e.target.value }))}
                className="w-full px-3 py-2 bg-secondary border border-border rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Eind Datum (optioneel)</label>
              <input
                type="date"
                value={form.end_date}
                onChange={(e) => setForm(prev => ({ ...prev, end_date: e.target.value }))}
                className="w-full px-3 py-2 bg-secondary border border-border rounded-lg"
              />
            </div>
          </div>

          {/* Active Toggle */}
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setForm(prev => ({ ...prev, active: !prev.active }))}
              className={`relative w-12 h-6 rounded-full transition-colors ${
                form.active ? 'bg-green-500' : 'bg-secondary'
              }`}
            >
              <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                form.active ? 'left-7' : 'left-1'
              }`} />
            </button>
            <span className="text-sm">Actief weergeven</span>
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-4 border-t border-border">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-secondary hover:bg-secondary/80 rounded-lg transition-colors"
          >
            Annuleren
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving || uploadingImage}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90 disabled:opacity-50 transition-opacity"
          >
            {saving || uploadingImage ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {uploadingImage ? 'Uploading...' : 'Opslaan...'}
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Opslaan
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminHotspots;
